import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { db } from "../../firebase";
import {
  doc, getDoc, updateDoc, serverTimestamp,
  query, where, collection, getDocs, addDoc, onSnapshot,
} from "firebase/firestore";
import { assignTechnicians, closeJob, rejectCompletion, addRemark, addDefectiveItem, endJob, acceptJob, startWork } from "./serviceTaskService";
import DefectiveItemForm from "../../components/DefectiveItemForm";
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { useCompany } from "../../context/CompanyContext";
import { markNotificationReadByReference } from "../../services/notificationService";
import { createSparePartRequest } from "../../services/sparePartService";

// ── helpers ──────────────────────────────────────────────────────────────────
const STATUS_CFG = {
  open:                       { label: "Open",             bg: "#E8F4FD", color: "#1565C0", dot: "#1976D2" },
  assigned:                   { label: "Assigned",         bg: "#FFF8E1", color: "#E65100", dot: "#FF8F00" },
  accepted:                   { label: "Accepted",         bg: "#E3F2FD", color: "#0277BD", dot: "#0288D1" },
  in_progress:                { label: "In Progress",      bg: "#FFF3E0", color: "#BF360C", dot: "#F4511E" },
  completed_pending_approval: { label: "Pending Approval", bg: "#F3E5F5", color: "#6A1B9A", dot: "#8E24AA" },
  closed:                     { label: "Closed",           bg: "#E8F5E9", color: "#1B5E20", dot: "#2E7D32" },
  cancelled:                  { label: "Cancelled",        bg: "#FFEBEE", color: "#B71C1C", dot: "#C62828" },
  rework:                     { label: "Rework",           bg: "#FBE9E7", color: "#BF360C", dot: "#E64A19" },
};

function fmtDate(ts) {
  if (!ts) return null;
  try {
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return null; }
}
function fmtShort(ts) {
  if (!ts) return null;
  try {
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
  } catch { return null; }
}
function calcDuration(start, end) {
  if (!start) return null;
  try {
    const s = start.toDate ? start.toDate() : new Date(start);
    const e = end ? (end.toDate ? end.toDate() : new Date(end)) : new Date();
    const mins = Math.round((e - s) / 60000);
    if (mins < 0) return null; // stale/cross-session timestamps — don't show a negative number
    if (mins < 60) return `${mins}m`;
    return `${Math.floor(mins / 60)}h ${mins % 60}m`;
  } catch { return null; }
}

// ── LiveTimer ─────────────────────────────────────────────────────────────────
function LiveTimer({ startedAt }) {
  const [elapsed, setElapsed] = useState("");
  useEffect(() => {
    if (!startedAt) return;
    const tick = () => setElapsed(calcDuration(startedAt, null) || "");
    tick();
    const iv = setInterval(tick, 30000);
    return () => clearInterval(iv);
  }, [startedAt]);
  if (!elapsed) return null;
  return (
    <div style={{ display:"flex", alignItems:"center", gap:8, background:"#FFF3E0", border:"1px solid #FFCC80", borderRadius:8, padding:"6px 12px" }}>
      <span style={{ width:8, height:8, borderRadius:"50%", background:"#F4511E", display:"inline-block", animation:"pulse 1.4s infinite" }} />
      <span style={{ fontSize:12, color:"#BF360C", fontWeight:600 }}>Live · {elapsed} elapsed</span>
    </div>
  );
}

// ── Stat Card ─────────────────────────────────────────────────────────────────
function StatCard({ icon, label, value, accent = "#6366F1", sub }) {
  return (
    <div style={{ background:"#fff", border:"1px solid #F1F1F1", borderRadius:12, padding:"14px 16px", minWidth:0, flex:1 }}>
      <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:6 }}>
        <span style={{ fontSize:18 }}>{icon}</span>
        <span style={{ fontSize:11, color:"#9CA3AF", fontWeight:600, textTransform:"uppercase", letterSpacing:"0.04em" }}>{label}</span>
      </div>
      <div style={{ fontSize:15, fontWeight:700, color:"#111827", lineHeight:1.3 }}>{value || "—"}</div>
      {sub && <div style={{ fontSize:11, color:"#9CA3AF", marginTop:2 }}>{sub}</div>}
    </div>
  );
}

// ── Timeline dot row ──────────────────────────────────────────────────────────
function TlRow({ label, value, done, active }) {
  return (
    <div style={{ display:"flex", alignItems:"center", gap:10, padding:"7px 0", borderBottom:"1px solid #F9FAFB" }}>
      <div style={{
        width:10, height:10, borderRadius:"50%", flexShrink:0,
        background: active ? "#F4511E" : done ? "#10B981" : "#E5E7EB",
        boxShadow: active ? "0 0 0 3px #FEE2E2" : "none",
      }} />
      <span style={{ fontSize:12, color:"#6B7280", width:80, flexShrink:0 }}>{label}</span>
      <span style={{ fontSize:12, color: done || active ? "#111827" : "#D1D5DB", fontWeight: done || active ? 600 : 400 }}>
        {value || "—"}
      </span>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
export default function ServiceDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { company } = useCompany();

  const [service, setService]           = useState(null);
  const [techs,   setTechs]             = useState([]);
  const [machines, setMachines]         = useState([]);
  const [selectedTech, setSelectedTech] = useState("");
  const [remarkText,   setRemarkText]   = useState("");
  const [savingRemark, setSavingRemark] = useState(false);
  const [showDefectiveForm, setShowDefectiveForm] = useState(false);
  const [addingDefective, setAddingDefective] = useState(false); // prevents double-click → duplicate defective_items entries
  const [defectiveItem, setDefectiveItem] = useState({
    item_name:"", qty:1, machine_id:"", machine_description:"", machine_model:"",
    serial_no:"", part_name:"", part_code:"", remarks:"",
  });

  // Spare Part request — reuses the SAME governed createSparePartRequest()
  // that TaskDetail.js (technician) uses. Lets a manager raise a spare
  // part request on behalf of a busy/unreachable technician.
  const [showSparePartForm, setShowSparePartForm] = useState(false);
  const [submittingSparePart, setSubmittingSparePart] = useState(false);
  const [sparePartItem, setSparePartItem] = useState({
    part_name: "", part_no: "", serial_no: "", brand: "",
    quantity: 1, machine_id: "", notes: "", linked_defective_item_id: "",
  });
  const [showCloseForm,  setShowCloseForm]  = useState(false);
  const [closeData,      setCloseData]      = useState({
    job_type:"", job_type_other:"",
    is_chargeable:false,
    // Line-item breakdown replaces the old single chargeable_amount field —
    // e.g. { description: "Gas Charging", amount: 2000 }. Sum of these
    // becomes chargeable_amount when closeJob() is called. Empty rows are
    // filtered out before submit.
    line_items: [{ description: "", amount: "" }],
  });
  const [closing,        setClosing]        = useState(false);
  const [showRejectForm, setShowRejectForm] = useState(false);
  const [rejectReason,   setRejectReason]   = useState("");

  // Manager/Owner fill-on-behalf-of-technician report state — lets a
  // manager submit the same completion report a technician would, when
  // the assigned tech is busy/unavailable. Reuses the SAME governed
  // endJob() function technician's TaskDetail.js calls — no new engine.
  const [showManagerReportForm, setShowManagerReportForm] = useState(false);
  const [managerReportData, setManagerReportData] = useState({
    issue_found: "", resolution: "", service_report: "", completion_status: "completed",
  });
  const [managerPayment, setManagerPayment] = useState({ received: null, amount: "", mode: "cash" });
  const [managerSubmitting, setManagerSubmitting] = useState(false);
  const [managerActionBusy, setManagerActionBusy] = useState(false); // Accept/Start on-behalf loading guard

  const [sparePartRequests, setSparePartRequests] = useState([]);
  const [siteMachinesForDefective, setSiteMachinesForDefective] = useState([]);

  const storage = getStorage();
  const user = JSON.parse(localStorage.getItem("user") || "{}");

  // Real-time listener — remarks (and status/timeline changes) update live
  // without needing a manual refresh, so the remark thread feels like chat.
  useEffect(() => {
    fetchTechs();
    if (!id || id === "create") return;
    const unsubscribe = onSnapshot(doc(db, "service_tasks", id), (snap) => {
      if (snap.exists()) {
        const data = { id: snap.id, ...snap.data() };
        if (data.company_id !== user.company_id) {
          alert("Access denied — this job belongs to a different company");
          navigate("/services");
          return;
        }
        setService(data);
        fetchMachines(data.machine_ids || []);
      }
    }, (err) => console.error("ServiceDetail onSnapshot error:", err.message));
    return () => unsubscribe();
  }, [id]);

  // Opening this job resolves any pending "new remark" notification for it —
  // viewing the chat thread counts as having seen the message.
  useEffect(() => {
    if (!id || id === "create") return;
    markNotificationReadByReference(id, "new_remark", user.company_id);
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Spare Parts — read-only for the manager view. Raising a request is
  // technician-only (TaskDetail.js); this just surfaces status so the
  // manager can see what's been requested for this job.
  useEffect(() => {
    if (!id || id === "create") return;
    fetchSparePartRequestsForJob();
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Registered machines at this job's site — used to populate the
  // "Select Machine" dropdown in the Defective Item form, so the item
  // links to a real site_machines doc_id (needed later for the Spare
  // Parts "Send for Repair" flow to find the currently-installed part).
  useEffect(() => {
    if (!service?.site_id) return;
    fetchSiteMachinesForDefective(service.site_id);
  }, [service?.site_id]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchSiteMachinesForDefective = async (site_id) => {
    try {
      const snap = await getDocs(query(
        collection(db, "site_machines"),
        where("company_id", "==", user.company_id),
        where("site_id", "==", site_id)
      ));
      setSiteMachinesForDefective(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (err) {
      console.warn("Site machines fetch failed:", err.message);
    }
  };

  const fetchSparePartRequestsForJob = async () => {
    try {
      const snap = await getDocs(query(collection(db, "spare_part_requests"), where("company_id", "==", user.company_id), where("service_id", "==", id)));
      const requests = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      requests.sort((a, b) => (b.requested_at?.seconds || 0) - (a.requested_at?.seconds || 0));
      setSparePartRequests(requests);
    } catch (err) {
      console.warn("Spare part fetch failed (non-blocking):", err.message);
    }
  };

  // Kept for places that call loadService() after an action (no-op now —
  // onSnapshot already keeps service fresh — retained so existing calls don't break).
  const loadService = async () => {};

  const fetchMachines = async (machineIds) => {
    if (!machineIds.length) { setMachines([]); return; }
    try {
      const results = await Promise.all(
        machineIds.map(async (mid) => {
          const mSnap = await getDoc(doc(db, "site_machines", mid));
          return mSnap.exists() ? { id: mSnap.id, ...mSnap.data() } : null;
        })
      );
      setMachines(results.filter(Boolean));
    } catch (err) { console.error("fetchMachines error:", err.message); }
  };

  const fetchTechs = async () => {
    if (!user.company_id) return;
    try {
      const q = query(collection(db,"users"), where("role","==","technician"), where("company_id","==",user.company_id));
      const snap = await getDocs(q);
      setTechs(snap.docs.map(d => ({ id:d.id, ...d.data(), uid:d.data().uid||"" })));
    } catch (err) { console.error(err.message); }
  };

  const handleAssign = async () => {
    if (!selectedTech) { alert("Select technician ❌"); return; }
    const tech = techs.find(t => t.id === selectedTech);
    const techSnap = await getDoc(doc(db,"users",tech.id));
const techUid = techSnap.data()?.uid || "";
if (!techUid) { alert("Technician has not logged in yet. Ask them to login first."); return; }
    const isReassign = ["assigned","accepted"].includes(service.status);
    try {
      await assignTechnicians({ task_id:id, techs:[{ id:techUid, name:tech.name, role:"technician" }], assigned_by:user.uid||user.id, assigned_by_name:user.name });

      // Purani unresolved job_assigned notifications resolve karo
      // D-302: company_id filter — the notifications read rule is
      // sameCompany(), so a query without it is rejected as a whole
      // ("insufficient permissions") and the new job_assigned
      // notification below was never created.
      const oldNotifSnap = await getDocs(query(
        collection(db, "notifications"),
        where("company_id", "==", user.company_id || ""),
        where("reference_id", "==", id),
        where("type", "==", "job_assigned"),
        where("resolved", "==", false)
      ));
      for (const n of oldNotifSnap.docs) {
        await updateDoc(n.ref, { resolved: true, is_read: true });
      }

      await addDoc(collection(db,"notifications"), {
        type:"job_assigned", notification_class:"action_required",
        title:`Task ${isReassign?"Reassigned":"Assigned"} — ${company?.company_name||user.company_name||""}`,
        body:`${service.complaint||"Service Job"} at ${service.site_name||""}`,
        for_roles:["technician"], for_uid: techUid,
        company_id:user.company_id, reference_id:id, reference_type:"service_task",
        route:`/tasks/${id}`, is_read:false, resolved:false, created_at:serverTimestamp(),
        metadata:{ site_name:service.site_name||"", complaint:service.complaint||"", scheduled_date:service.scheduled_date||"", assigned_by:user.name },
      });
      alert(isReassign ? "Technician Reassigned ✅" : "Technician Assigned ✅");
setSelectedTech("");
loadService();
    } catch (err) { alert("Error: "+err.message); }
  };

  // Uses the same governed addRemark() as the technician side (serviceTaskService.js)
  // — single schema { text, by, by_name, at } for remarks_log, so both sides
  // can correctly tell "my message" vs "their message" apart.
  const handleRemark = async () => {
    if (!remarkText.trim()) return;
    setSavingRemark(true);
    try {
      await addRemark({
        task_id:       id,
        remark:        remarkText.trim(),
        added_by:      user.uid || user.id || "",
        added_by_name: user.name || "",
      });
      setRemarkText(""); loadService();
    } catch (err) { alert("Error: "+err.message); }
    finally { setSavingRemark(false); }
  };


  const handleMRF = () => navigate("/mrf", { state:{ type:"service", ref_id:service.id } });

  // Navigation target for "add a machine at this site" — mirrors
  // TaskDetail.js's goToAddMachine(), just callable from the manager side.
  const goToAddMachine = () => {
    const params = new URLSearchParams({
      client_id:   service?.client_id   || "",
      client_name: service?.client_name || "",
      site_id:     service?.site_id     || "",
      site_name:   service?.site_name   || "",
      task_id:     id,
    });
    navigate(`/machines/add?${params.toString()}`);
  };

  // Prefill the spare-part form from an existing defective item — avoids
  // retyping part_name/part_code/serial_no the tech (or manager) already
  // recorded. Mirrors TaskDetail.js's prefillSparePartFromDefective().
  const prefillSparePartFromDefective = (item, idx) => {
    setSparePartItem({
      part_name:   item.item_name || "",
      part_no:     item.part_code || "",
      serial_no:   item.serial_no || "",
      brand:       item.part_name || "",
      quantity:    item.qty || 1,
      machine_id:  item.machine_id || "",
      notes:       `From defective item: ${item.item_name || ""}`,
      linked_defective_item_id: String(idx),
    });
    setShowSparePartForm(true);
  };

  // Reuses the SAME governed createSparePartRequest() function
  // (sparePartService.js) that the technician's TaskDetail.js already
  // calls — no new write-path, just a manager-accessible entry point.
  const submitSparePart = async () => {
    if (submittingSparePart) return;
    if (!sparePartItem.part_name.trim()) { alert("❌ Part name required"); return; }
    if (!sparePartItem.serial_no.trim()) { alert("❌ Serial No. required — this system tracks serialized parts only"); return; }
    if (!sparePartItem.quantity || Number(sparePartItem.quantity) < 1) { alert("❌ Quantity must be at least 1"); return; }

    const duplicate = sparePartRequests.find(r =>
      r.status !== "closed" &&
      (r.part_name || "").trim().toLowerCase() === sparePartItem.part_name.trim().toLowerCase()
    );
    if (duplicate) {
      const proceed = window.confirm(
        `⚠️ A similar open request already exists for "${sparePartItem.part_name}" (${duplicate.request_no}, status: ${duplicate.status.replace(/_/g, " ")}).\n\nSubmit this as a separate request anyway?`
      );
      if (!proceed) return;
    }

    setSubmittingSparePart(true);
    try {
      await createSparePartRequest({
        company_id:   user.company_id || "",
        service_id:   id,
        project_id:   service?.project_id || "",
        site_id:      service?.site_id || "",
        client_id:    service?.client_id || "",
        machine_id:   sparePartItem.machine_id || "",
        part_name:    sparePartItem.part_name.trim(),
        part_no:      sparePartItem.part_no.trim(),
        serial_no:    sparePartItem.serial_no.trim(),
        brand:        sparePartItem.brand.trim(),
        quantity:     Number(sparePartItem.quantity),
        linked_defective_item_id: sparePartItem.linked_defective_item_id || "",
        notes:        sparePartItem.notes.trim(),
        requested_by: user.uid || user.id || "",
        requested_by_name: user.name || "",
      });
      setSparePartItem({ part_name:"", part_no:"", serial_no:"", brand:"", quantity:1, machine_id:"", notes:"", linked_defective_item_id:"" });
      setShowSparePartForm(false);
      alert("✅ Spare part requested!");
      fetchSparePartRequestsForJob();
    } catch (err) {
      alert("Error: " + err.message);
    } finally {
      setSubmittingSparePart(false);
    }
  };

  const MAX_PHOTOS_PER_JOB = 5;

  const handlePhotoUpload = async (e) => {
    if (service.status !== "in_progress") { alert("Start work first ❌"); return; }
    const files = e.target.files; if (!files.length) return;

    const existingCount = (service.photos || []).length;
    const remainingSlots = MAX_PHOTOS_PER_JOB - existingCount;

    if (remainingSlots <= 0) {
      alert(`❌ Maximum ${MAX_PHOTOS_PER_JOB} photos already uploaded for this job.`);
      e.target.value = "";
      return;
    }

    // Only take as many files as fit within the 5-photo cap — rest are
    // silently skipped with a warning, rather than blocking the whole
    // upload (so a technician selecting 6 photos still gets the first 5).
    const filesToUpload = Array.from(files).slice(0, remainingSlots);
    const skippedCount = files.length - filesToUpload.length;

    const urls = [];
    for (let file of filesToUpload) {
      // Unique storage path per upload (timestamp + original name) —
      // prevents mobile-camera photos (which often reuse the same
      // filename, e.g. "image.jpg") from silently overwriting each
      // other in Storage, which was the confirmed cause of only 1
      // photo actually persisting even when multiple were selected.
      const uniqueName = `${Date.now()}_${file.name}`;
      const r = ref(storage, `services/${id}/${uniqueName}`);
      await uploadBytes(r, file);
      urls.push(await getDownloadURL(r));
    }
    await updateDoc(doc(db,"service_tasks",id), { photos:[...(service.photos||[]),...urls] });

    if (skippedCount > 0) {
      alert(`✅ ${filesToUpload.length} photo(s) uploaded. ⚠️ ${skippedCount} skipped — max ${MAX_PHOTOS_PER_JOB} photos per job.`);
    } else {
      alert("Photos Uploaded ✅");
    }
    e.target.value = "";
    loadService();
  };

  const addDefective = async () => {
    if (addingDefective) return;
    if (!defectiveItem.item_name.trim()) { alert("Item name required ❌"); return; }
    setAddingDefective(true);
    try {
      await addDefectiveItem({
        task_id:             id,
        item_name:           defectiveItem.item_name.trim(),
        qty:                 Number(defectiveItem.qty || 1),
        machine_id:          defectiveItem.machine_id || "",
        machine_description: defectiveItem.machine_description.trim(),
        machine_model:       defectiveItem.machine_model.trim(),
        serial_no:           defectiveItem.serial_no.trim(),
        part_name:           defectiveItem.part_name.trim(),
        part_code:           defectiveItem.part_code.trim(),
        remarks:             defectiveItem.remarks.trim(),
      });
      setDefectiveItem({ item_name:"",qty:1,machine_id:"",machine_description:"",machine_model:"",serial_no:"",part_name:"",part_code:"",remarks:"" });
      setShowDefectiveForm(false); loadService(); alert("✅ Defective item added!");
    } catch (err) { alert("Error: "+err.message); }
    finally { setAddingDefective(false); }
  };

  const addCloseLineItem = () =>
    setCloseData(p => ({ ...p, line_items: [...p.line_items, { description: "", amount: "" }] }));

  const removeCloseLineItem = (i) =>
    setCloseData(p => ({ ...p, line_items: p.line_items.filter((_, idx) => idx !== i) }));

  const updateCloseLineItem = (i, field, val) =>
    setCloseData(p => {
      const updated = [...p.line_items];
      updated[i] = { ...updated[i], [field]: val };
      return { ...p, line_items: updated };
    });

  const closeLineItemsTotal = closeData.line_items
    .reduce((s, li) => s + Number(li.amount || 0), 0);

  const handleCloseJob = async () => {
    if (!closeData.job_type) { alert("Job type select karo ❌"); return; }

    const validLineItems = closeData.line_items.filter(
      li => li.description.trim() && Number(li.amount) > 0
    );
    if (closeData.is_chargeable && validLineItems.length === 0) {
      alert("Chargeable service ke liye kam se kam ek line item (description + amount) daalo ❌");
      return;
    }

    setClosing(true);
    try {
      const totalAmount = validLineItems.reduce((s, li) => s + Number(li.amount), 0);

      await closeJob({
        task_id:           id,
        job_type:          closeData.job_type,
        job_type_other:    closeData.job_type_other,
        is_chargeable:     closeData.is_chargeable,
        chargeable_amount: totalAmount,
        closed_by:         user.uid || user.id,
        closed_by_name:    user.name,
      });

      setShowCloseForm(false);

      // Chargeable job → hand off to the Invoice Engine (single governed
      // billing system — see D-2XX). Non-chargeable jobs (AMC/Warranty/
      // Internal/Not Chargeable) just close, no invoice needed.
      if (closeData.is_chargeable && validLineItems.length > 0) {
        navigate("/billing/create", {
          state: {
            from_service:        true,
            service_id:          id,
            client_id:           service.client_id   || "",
            client_name:         service.client_name || "",
            site_id:             service.site_id      || "",
            site_name:           service.site_name    || "",
            project_id:          service.project_id   || "",
            project_name:        service.project_name || "",
            service_complaint:   service.complaint     || "",
            job_type:            closeData.job_type,
            close_line_items:    validLineItems, // [{ description, amount }]
            // If the technician already collected cash/UPI at site, that
            // amount is passed through so the invoice's "Paid" field is
            // pre-filled correctly — Manager doesn't need to separately
            // remember to "Record Payment" for money already collected.
            already_collected_amount: service.site_payment_received
              ? Number(service.site_payment_amount || 0)
              : 0,
          },
        });
      } else {
        alert("✅ Job Closed!");
        loadService();
      }
    } catch (err) { alert("Error: " + err.message); }
    finally { setClosing(false); }
  };

  const handleRejectCompletion = async () => {
    if (!rejectReason.trim()) { alert("Reason required ❌"); return; }
    try {
      await rejectCompletion({ task_id:id, reason:rejectReason.trim(), rejected_by:user.uid||user.id, rejected_by_name:user.name });
      alert("✅ Sent back!"); setShowRejectForm(false); setRejectReason(""); loadService();
    } catch (err) { alert("Error: "+err.message); }
  };

  // Manager fills the completion report on behalf of the assigned
  // technician (e.g. tech is busy/unreachable). Calls the SAME endJob()
  // the technician's own "Complete Job" button calls — tech_id/tech_name
  // stay the ASSIGNED technician's (so duration/visit-log attribution is
  // correct); filled_by/filled_by_name/filled_by_role record who actually
  // submitted it, for audit purposes.
  // Manager Accepts on behalf of the assigned technician (office-managed
  // mode) — reuses the SAME governed acceptJob() function, just called
  // by a manager instead of the tech. Audit fields record who actually
  // clicked it.
  const handleManagerAccept = async () => {
    const assignedTech = service.assigned_techs?.[0];
    if (!assignedTech?.id) { alert("❌ No technician assigned to this job"); return; }
    setManagerActionBusy(true);
    try {
      await acceptJob({
        task_id: id, tech_id: assignedTech.id, tech_name: assignedTech.name || "",
        filled_by: user.uid || user.id || "", filled_by_name: user.name || "", filled_by_role: user.role || "",
      });
      alert("✅ Accepted on behalf of " + (assignedTech.name || "technician"));
      loadService();
    } catch (err) { alert("Error: " + err.message); }
    finally { setManagerActionBusy(false); }
  };

  // Manager Starts on behalf of the assigned technician — reuses the
  // SAME governed startWork() function with manager_initiated:true,
  // which skips the geofence check (no meaningful tech-location to
  // verify when the office is starting the job, not the tech).
  const handleManagerStart = async () => {
    const assignedTech = service.assigned_techs?.[0];
    if (!assignedTech?.id) { alert("❌ No technician assigned to this job"); return; }
    setManagerActionBusy(true);
    try {
      await startWork({
        task_id: id, tech_id: assignedTech.id, tech_name: assignedTech.name || "",
        company_id: user.company_id || "",
        manager_initiated: true,
        filled_by: user.uid || user.id || "", filled_by_name: user.name || "", filled_by_role: user.role || "",
      });
      alert("✅ Job Started on behalf of " + (assignedTech.name || "technician"));
      loadService();
    } catch (err) { alert("Error: " + err.message); }
    finally { setManagerActionBusy(false); }
  };

  const handleManagerCompleteJob = async () => {
    if (managerSubmitting) return;
    if (!managerReportData.service_report?.trim()) {   // D-308: only the detailed report is required
      alert("❌ Please fill all required fields"); return;
    }
    if (managerPayment.received === null) { alert("❌ Payment received — Yes ya No select karo"); return; }
    if (managerPayment.received && !managerPayment.amount) { alert("❌ Amount enter karo"); return; }

    const assignedTech = service.assigned_techs?.[0];
    if (!assignedTech?.id) { alert("❌ No technician assigned to this job"); return; }

    setManagerSubmitting(true);
    try {
      await endJob({
        task_id:               id,
        tech_id:               assignedTech.id,
        tech_name:             assignedTech.name || "",
        company_id:            user.company_id || "",
        completion_type:       managerReportData.completion_status === "partial" ? "partial" : "complete",
        service_report:        managerReportData.service_report,
        issue_found:           managerReportData.issue_found,
        resolution:            managerReportData.resolution,
        site_payment_received: Boolean(managerPayment.received),
        site_payment_amount:   managerPayment.received ? Number(managerPayment.amount) : 0,
        site_payment_mode:     managerPayment.received ? managerPayment.mode : "",
        filled_by:             user.uid || user.id || "",
        filled_by_name:        user.name || "",
        filled_by_role:        user.role || "",
      });
      alert("✅ Report Submitted on behalf of " + (assignedTech.name || "technician") + "!");
      setShowManagerReportForm(false);
      loadService();
    } catch (err) { alert("Error: " + err.message); }
    finally { setManagerSubmitting(false); }
  };

  if (!service) return <div style={{ display:"flex", alignItems:"center", justifyContent:"center", height:200, color:"#9CA3AF", fontSize:14 }}>Loading...</div>;

  const isTech    = user.role === "technician";
  const isManager = ["sm","owner","manager"].includes(user.role);
  const myUid     = user.uid || user.id;
  const assignedUids = service.assigned_techs?.map(t => t.id) || [];
  const isAssignedTech = isTech && (service.assigned_to === myUid || assignedUids.includes(myUid));

  const st  = STATUS_CFG[service.status] || STATUS_CFG.open;
  const techNames = service.assigned_techs?.length > 0
    ? service.assigned_techs.map(t => t.name).join(", ")
    : service.assigned_to_name || "Not Assigned";
  const formatMinutes = (mins) => {
    if (mins < 0) return null;
    if (mins < 60) return `${mins}m`;
    return `${Math.floor(mins / 60)}h ${mins % 60}m`;
  };
  const totalDur = service.duration_minutes
    ? formatMinutes(service.duration_minutes)
    : (service.started_at && service.completed_at)
      ? calcDuration(service.started_at, service.completed_at)
      : null;

  const card = { background:"#fff", border:"1px solid #F1F1F1", borderRadius:14, padding:"16px 18px" };
  const label = { fontSize:11, color:"#9CA3AF", fontWeight:600, textTransform:"uppercase", letterSpacing:"0.05em", marginBottom:10, display:"block" };
  const inp = { border:"1px solid #E5E7EB", borderRadius:8, padding:"8px 10px", width:"100%", fontSize:13, outline:"none", boxSizing:"border-box", color:"#111827" };

  return (
    <div style={{ padding:"16px 20px", background:"#F8F9FB", minHeight:"100vh", fontFamily:"system-ui,sans-serif" }}>
      <style>{`@keyframes pulse{0%,100%{opacity:1}50%{opacity:.4}}`}</style>

      {/* ── TOP HEADER BAR ── */}
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:16, flexWrap:"wrap", gap:8 }}>
        <div>
          <div style={{ fontSize:18, fontWeight:700, color:"#111827" }}>
            {service.task_no || service.service_no || "Service Job"}
          </div>
          <div style={{ fontSize:12, color:"#9CA3AF", marginTop:2 }}>{fmtDate(service.created_at)}</div>
        </div>
        <div style={{ display:"flex", alignItems:"center", gap:8 }}>
          {service.status === "in_progress" && service.started_at && <LiveTimer startedAt={service.started_at} />}
          <span style={{ background:st.bg, color:st.color, fontSize:12, fontWeight:700, padding:"5px 14px", borderRadius:20, display:"flex", alignItems:"center", gap:6 }}>
            <span style={{ width:7, height:7, borderRadius:"50%", background:st.dot, display:"inline-block" }} />
            {st.label}
          </span>
        </div>
      </div>

      {/* ── STAT CHIPS ROW ── */}
      <div style={{ display:"flex", gap:10, marginBottom:16, flexWrap:"wrap" }}>
        <StatCard icon="🏢" label="Client"   value={service.client_name} />
        <StatCard icon="📍" label="Site"     value={service.site_name} />
        <StatCard icon="👷" label="Assigned" value={techNames} />
        {totalDur && <StatCard icon="⏱" label="Duration" value={totalDur} />}
      </div>

      {/* ── MAIN 2-COL GRID ── */}
      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:14 }}>

        {/* LEFT COL */}
        <div style={{ display:"flex", flexDirection:"column", gap:14 }}>

          {/* JOB DETAILS */}
          <div style={card}>
            <span style={label}>Job Details</span>
            {[
              { l:"Complaint",   v:service.complaint },
              { l:"Description", v:service.description },
              { l:"Job Type",    v:service.job_type },
              { l:"Scheduled",   v:service.scheduled_date },
              { l:"Created By",  v:service.created_by_name },
              { l:"Project",     v:service.project_name },
              { l:"AMC",         v:service.amc_contract_no },
            ].filter(r => r.v).map((r,i) => (
              <div key={i} style={{ display:"flex", gap:8, padding:"6px 0", borderBottom:"1px solid #F9FAFB" }}>
                <span style={{ fontSize:12, color:"#9CA3AF", width:90, flexShrink:0 }}>{r.l}</span>
                <span style={{ fontSize:13, color:"#111827", fontWeight:500 }}>{r.v}</span>
              </div>
            ))}
            {service.location_link && (
              <a href={service.location_link} target="_blank" rel="noreferrer"
                style={{ fontSize:12, color:"#3B82F6", display:"block", marginTop:8, textDecoration:"none" }}>
                📍 Open Location →
              </a>
            )}
          </div>

          {/* TIMELINE */}
          <div style={card}>
            <span style={label}>Timeline</span>
            <TlRow label="Created"   value={fmtShort(service.created_at)}   done={!!service.created_at}   active={false} />
            <TlRow label="Assigned"  value={fmtShort(service.assigned_at)}  done={!!service.assigned_at}  active={service.status==="assigned"} />
            <TlRow label="Started"   value={fmtShort(service.started_at)}   done={!!service.started_at}   active={service.status==="in_progress"} />
            <TlRow label="Completed" value={fmtShort(service.completed_at)} done={!!service.completed_at} active={service.status==="completed_pending_approval"} />
            <TlRow label="Closed"    value={fmtShort(service.closed_at)}    done={!!service.closed_at}    active={false} />
          </div>

          {/* PHOTOS */}
          {service.photos?.length > 0 && (
            <div style={card}>
              <span style={label}>Photos ({service.photos.length})</span>
              <div style={{ display:"flex", flexWrap:"wrap", gap:8 }}>
                {service.photos.map((url,i) => (
                  <a key={i} href={url} target="_blank" rel="noreferrer">
                    <img src={url} alt={`p${i}`} style={{ width:64, height:64, objectFit:"cover", borderRadius:8, border:"1px solid #F1F1F1" }} />
                  </a>
                ))}
              </div>
            </div>
          )}

          {/* MACHINES */}
          {machines.length > 0 && (
            <div style={card}>
              <span style={label}>🏭 Machines ({machines.length})</span>
              <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
                {machines.map((m) => (
                  <div key={m.id} onClick={() => navigate(`/machines/${m.id}`)}
                    style={{ background:"#F9FAFB", border:"1px solid #F1F1F1", borderRadius:8, padding:"8px 10px", cursor:"pointer" }}>
                    <div style={{ fontSize:13, fontWeight:600, color:"#111827" }}>{m.machine_name || "Unnamed Machine"}</div>
                    <div style={{ fontSize:11, color:"#6B7280", marginTop:2 }}>
                      {m.brand || "—"} {m.model_no ? `· ${m.model_no}` : ""} {m.serial_no ? `· S/N ${m.serial_no}` : ""}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* RIGHT COL */}
        <div style={{ display:"flex", flexDirection:"column", gap:14 }}>

          {/* FINANCIALS */}
      {(service.job_type || service.site_payment_received) && (
        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6">
          <h2 className="text-base font-bold text-slate-800 mb-4">💰 Financials</h2>
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-green-50 rounded-xl p-4">
              <div className="text-xs text-slate-500 font-semibold uppercase tracking-wide mb-1">Job Type</div>
              <div className="text-base font-bold text-green-700 capitalize">{service.job_type ? service.job_type.replace(/_/g," ") : "—"}</div>
            </div>
            <div className="bg-blue-50 rounded-xl p-4">
              <div className="text-xs text-slate-500 font-semibold uppercase tracking-wide mb-1">Amount</div>
              <div className="text-base font-bold text-blue-700">
                {service.is_chargeable
                  ? `₹${Number(service.chargeable_amount || 0).toLocaleString("en-IN")}${service.amount_pending ? " (Pending)" : ""}`
                  : service.site_payment_received
                    ? `₹${Number(service.site_payment_amount || 0).toLocaleString("en-IN")}`
                    : "—"}
              </div>
            </div>
            {service.site_payment_mode && (
              <div className="bg-slate-50 rounded-xl p-4">
                <div className="text-xs text-slate-500 font-semibold uppercase tracking-wide mb-1">Payment Mode</div>
                <div className="text-base font-bold text-slate-700 uppercase">{service.site_payment_mode}</div>
              </div>
            )}
            {service.defective_items && service.defective_items.length > 0 && (
              <div className="bg-red-50 rounded-xl p-4 col-span-2">
                <div className="text-xs text-slate-500 font-semibold uppercase tracking-wide mb-2">⚠️ Defective Items</div>
                {service.defective_items.map((d, i) => (
                  <div key={i} className="text-sm text-red-700 font-medium">• {d.item_name || d} {d.qty ? `(${d.qty})` : ""}</div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

          {/* REMARKS — chat-style thread, shared schema with technician side */}
          <div style={card}>
            <span style={label}>💬 Remarks</span>
            <div style={{
              maxHeight:260, overflowY:"auto", marginBottom:10, padding:10,
              background:"#F8FAFC", borderRadius:8, display:"flex", flexDirection:"column", gap:8,
            }}>
              {(service.remarks_log?.length > 0) ? (
                service.remarks_log.map((r,i) => {
                  const myUidLocal = user.uid || user.id;
                  const isMine = r.by === myUidLocal;
                  return (
                    <div key={i} style={{ alignSelf: isMine ? "flex-end" : "flex-start", maxWidth:"85%" }}>
                      <div style={{
                        background: isMine ? "#111827" : "#E5E7EB",
                        color: isMine ? "#fff" : "#111827",
                        padding:"8px 12px", borderRadius:12,
                        borderBottomRightRadius: isMine ? 2 : 12,
                        borderBottomLeftRadius: isMine ? 12 : 2,
                        fontSize:13, wordBreak:"break-word",
                      }}>
                        {r.text}
                      </div>
                      <p style={{ fontSize:10, color:"#9CA3AF", margin:"2px 4px 0", textAlign: isMine ? "right" : "left" }}>
                        {r.by_name || "—"} · {r.at ? new Date(r.at).toLocaleString("en-IN",{day:"2-digit",month:"short",hour:"2-digit",minute:"2-digit"}) : ""}
                      </p>
                    </div>
                  );
                })
              ) : (
                <p style={{ fontSize:12, color:"#D1D5DB", margin:0, textAlign:"center" }}>No remarks yet.</p>
              )}
            </div>
            {(isAssignedTech || isManager) && (
              <div style={{ display:"flex", gap:8 }}>
                <input value={remarkText} onChange={e=>setRemarkText(e.target.value)}
                  onKeyDown={e => { if (e.key === "Enter") handleRemark(); }}
                  placeholder="Type a remark..."
                  style={{ ...inp, flex:1 }} />
                <button onClick={handleRemark} disabled={savingRemark||!remarkText.trim()}
                  style={{ background:"#111827", color:"#fff", border:"none", borderRadius:8, padding:"0 16px", fontSize:13, fontWeight:600, cursor:"pointer", opacity:remarkText.trim()?1:0.4 }}>
                  {savingRemark?"...":"Send"}
                </button>
              </div>
            )}
          </div>

          {/* ASSIGN / REASSIGN */}
          {isManager && !["in_progress","completed_pending_approval","closed","cancelled"].includes(service.status) && (
            <div style={card}>
              <span style={label}>{service.status==="assigned"?"Reassign Technician":"Assign Technician"}</span>
              {service.status==="assigned" && (
                <div style={{ fontSize:12, color:"#6B7280", marginBottom:8 }}>
                  Current: <b style={{ color:"#111827" }}>{techNames}</b>
                </div>
              )}
              <select value={selectedTech} onChange={e=>setSelectedTech(e.target.value)} style={{ ...inp, marginBottom:8 }}>
                <option value="">Select Technician</option>
                {techs.map(t=><option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
              <button onClick={handleAssign}
                style={{ background: service.status==="assigned"?"#F59E0B":"#3B82F6", color:"#fff", border:"none", borderRadius:8, padding:"10px 0", width:"100%", fontSize:13, fontWeight:600, cursor:"pointer" }}>
                {service.status==="assigned"?"🔄 Reassign":"Assign Technician"}
              </button>
            </div>
          )}

          {/* MANAGER — ACCEPT / START ON BEHALF OF TECHNICIAN (office-managed mode) */}
          {isManager && ["assigned"].includes(service.status) && (() => {
            const assignedTech = service.assigned_techs?.[0];
            const notYetAccepted = assignedTech && assignedTech.accepted !== true;
            const acceptedNotStarted = assignedTech && assignedTech.accepted === true;
            return (
              <div style={card}>
                <span style={label}>🖥 Office-Managed Actions</span>
                <div style={{ fontSize:12, color:"#6B7280", marginBottom:10 }}>
                  Use this if the technician can't operate the app right now — Owner/Manager can Accept and Start the job on their behalf. Location/geofence check is skipped for office-managed starts.
                </div>
                {notYetAccepted && (
                  <button onClick={handleManagerAccept} disabled={managerActionBusy}
                    style={{ background:"#3B82F6", color:"#fff", border:"none", borderRadius:8, padding:"10px 0", width:"100%", fontSize:13, fontWeight:600, cursor:"pointer", marginBottom:8 }}>
                    {managerActionBusy ? "..." : `✋ Accept on behalf of ${techNames}`}
                  </button>
                )}
                {acceptedNotStarted && (
                  <button onClick={handleManagerStart} disabled={managerActionBusy}
                    style={{ background:"#F59E0B", color:"#fff", border:"none", borderRadius:8, padding:"10px 0", width:"100%", fontSize:13, fontWeight:600, cursor:"pointer" }}>
                    {managerActionBusy ? "..." : `▶ Start Job on behalf of ${techNames}`}
                  </button>
                )}
              </div>
            );
          })()}

          {/* MANAGER — FILL REPORT ON BEHALF OF TECHNICIAN */}
          {isManager && service.status === "in_progress" && (
            <div style={card}>
              <span style={label}>📋 Fill Report (On Behalf of Technician)</span>
              {!showManagerReportForm ? (
                <button onClick={() => setShowManagerReportForm(true)}
                  style={{ background:"#0EA5E9", color:"#fff", border:"none", borderRadius:8, padding:"10px 0", width:"100%", fontSize:13, fontWeight:600, cursor:"pointer" }}>
                  🖊 Fill Job Report for {techNames}
                </button>
              ) : (
                <div>
                  <textarea placeholder="Issue Found (optional)" value={managerReportData.issue_found}
                    onChange={e => setManagerReportData(p => ({ ...p, issue_found: e.target.value }))}
                    rows={2} style={{ ...inp, resize:"none", marginBottom:8 }} />
                  <textarea placeholder="Work Done (optional)" value={managerReportData.resolution}
                    onChange={e => setManagerReportData(p => ({ ...p, resolution: e.target.value }))}
                    rows={2} style={{ ...inp, resize:"none", marginBottom:8 }} />
                  <textarea placeholder="Detailed Report *" value={managerReportData.service_report}
                    onChange={e => setManagerReportData(p => ({ ...p, service_report: e.target.value }))}
                    rows={3} style={{ ...inp, resize:"none", marginBottom:8 }} />
                  <select value={managerReportData.completion_status}
                    onChange={e => setManagerReportData(p => ({ ...p, completion_status: e.target.value }))}
                    style={{ ...inp, marginBottom:8 }}>
                    <option value="completed">Completed</option>
                    <option value="partial">Partial</option>
                    <option value="not_resolved">Not Resolved</option>
                  </select>

                  <div style={{ background:"#F0FDF4", border:"1px solid #BBF7D0", borderRadius:8, padding:10, marginBottom:8 }}>
                    <div style={{ fontSize:12, fontWeight:700, color:"#166534", marginBottom:8 }}>💰 Payment Collected? *</div>
                    <div style={{ display:"flex", gap:8, marginBottom:8 }}>
                      <button onClick={() => setManagerPayment(p => ({ ...p, received:true }))}
                        style={{ flex:1, padding:"8px 0", borderRadius:8, border:"2px solid", borderColor: managerPayment.received===true?"#16A34A":"#E5E7EB", background: managerPayment.received===true?"#16A34A":"#fff", color: managerPayment.received===true?"#fff":"#374151", fontWeight:700, cursor:"pointer", fontSize:13 }}>
                        ✅ Yes
                      </button>
                      <button onClick={() => setManagerPayment(p => ({ ...p, received:false, amount:"" }))}
                        style={{ flex:1, padding:"8px 0", borderRadius:8, border:"2px solid", borderColor: managerPayment.received===false?"#DC2626":"#E5E7EB", background: managerPayment.received===false?"#DC2626":"#fff", color: managerPayment.received===false?"#fff":"#374151", fontWeight:700, cursor:"pointer", fontSize:13 }}>
                        ❌ No
                      </button>
                    </div>
                    {managerPayment.received === true && (
                      <div style={{ display:"flex", gap:8 }}>
                        <input type="number" placeholder="Amount ₹ *" value={managerPayment.amount}
                          onChange={e => setManagerPayment(p => ({ ...p, amount: e.target.value }))}
                          style={{ ...inp, flex:1 }} />
                        <select value={managerPayment.mode}
                          onChange={e => setManagerPayment(p => ({ ...p, mode: e.target.value }))}
                          style={{ ...inp, flex:1 }}>
                          <option value="cash">Cash</option>
                          <option value="upi">UPI</option>
                          <option value="cheque">Cheque</option>
                          <option value="bank_transfer">Bank Transfer</option>
                        </select>
                      </div>
                    )}
                  </div>

                  <div style={{ display:"flex", gap:8 }}>
                    <button onClick={() => setShowManagerReportForm(false)}
                      style={{ flex:1, background:"#fff", border:"1px solid #E5E7EB", borderRadius:8, padding:"9px 0", fontSize:13, cursor:"pointer", color:"#6B7280" }}>
                      Cancel
                    </button>
                    <button onClick={handleManagerCompleteJob} disabled={managerSubmitting}
                      style={{ flex:1, background:"#0EA5E9", color:"#fff", border:"none", borderRadius:8, padding:"9px 0", fontSize:13, fontWeight:600, cursor:"pointer" }}>
                      {managerSubmitting ? "Submitting..." : "Submit Report"}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* TECH ACTIONS — also available to Manager/Owner while job is
              in_progress, so office-managed jobs can be fully handled
              without the technician ever touching the app. All actions
              below reuse the SAME governed functions the tech's own
              TaskDetail.js calls — no new write-paths. */}
          {(isAssignedTech || (isManager && service.status === "in_progress")) && (
            <div style={card}>
              <span style={label}>Actions{isManager && !isAssignedTech ? " (On Behalf of Technician)" : ""}</span>
              <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
                <button onClick={handleMRF}
                  style={{ background:"#7C3AED", color:"#fff", border:"none", borderRadius:8, padding:"10px 0", fontSize:13, fontWeight:600, cursor:"pointer" }}>
                  📦 Raise MRF
                </button>
                {isAssignedTech && (
                  <label style={{ background:"#2563EB", color:"#fff", borderRadius:8, padding:"10px 0", fontSize:13, fontWeight:600, cursor:"pointer", textAlign:"center", display:"block" }}>
                    📷 Upload Photos ({(service.photos || []).length}/5)
                    <input type="file" multiple accept="image/*" className="hidden" onChange={handlePhotoUpload} style={{ display:"none" }} />
                  </label>
                )}
                <button onClick={()=>setShowDefectiveForm(!showDefectiveForm)}
                  style={{ background:"#DC2626", color:"#fff", border:"none", borderRadius:8, padding:"10px 0", fontSize:13, fontWeight:600, cursor:"pointer" }}>
                  🔧 {showDefectiveForm?"Cancel":"Add Defective Item"}
                </button>
                <button onClick={()=>setShowSparePartForm(!showSparePartForm)}
                  style={{ background:"#0891B2", color:"#fff", border:"none", borderRadius:8, padding:"10px 0", fontSize:13, fontWeight:600, cursor:"pointer" }}>
                  🔩 {showSparePartForm?"Cancel":"Request Spare Part"}
                </button>
                <button onClick={goToAddMachine}
                  style={{ background:"#0EA5E9", color:"#fff", border:"none", borderRadius:8, padding:"10px 0", fontSize:13, fontWeight:600, cursor:"pointer" }}>
                  🖥 Add Machine at this Site
                </button>
              </div>

              {showDefectiveForm && (
                <DefectiveItemForm
                  defectiveItem={defectiveItem}
                  setDefectiveItem={setDefectiveItem}
                  siteMachines={siteMachinesForDefective}
                  onSave={addDefective}
                  inputStyle={{ ...inp, marginBottom: 6 }}
                  containerStyle={{ marginTop:12, background:"#FEF2F2", border:"1px solid #FECACA", borderRadius:10, padding:12 }}
                  sectionLabelStyle={{ fontSize:11, fontWeight:700, color:"#991B1B", marginTop:10, marginBottom:6, textTransform:"uppercase", letterSpacing:"0.05em" }}
                  saveButtonStyle={{ background:"#DC2626", color:"#fff", border:"none", borderRadius:8, padding:"10px 0", width:"100%", fontSize:13, fontWeight:600, cursor: addingDefective ? "not-allowed" : "pointer", opacity: addingDefective ? 0.6 : 1 }}
                  saveLabel={addingDefective ? "Saving..." : "Save Defective Item"}
                  saveDisabled={addingDefective}
                />
              )}

              {showSparePartForm && (
                <div style={{ marginTop:12, padding:12, background:"#ECFEFF", border:"1px solid #67E8F9", borderRadius:10 }}>
                  <input placeholder="Part Name *" value={sparePartItem.part_name}
                    onChange={e => setSparePartItem(p => ({ ...p, part_name: e.target.value }))}
                    style={{ ...inp, marginBottom:6 }} />
                  <input placeholder="Part No." value={sparePartItem.part_no}
                    onChange={e => setSparePartItem(p => ({ ...p, part_no: e.target.value }))}
                    style={{ ...inp, marginBottom:6 }} />
                  <input placeholder="Serial No. *" value={sparePartItem.serial_no}
                    onChange={e => setSparePartItem(p => ({ ...p, serial_no: e.target.value }))}
                    style={{ ...inp, marginBottom:6 }} />
                  <input placeholder="Brand" value={sparePartItem.brand}
                    onChange={e => setSparePartItem(p => ({ ...p, brand: e.target.value }))}
                    style={{ ...inp, marginBottom:6 }} />
                  <input type="number" placeholder="Quantity" value={sparePartItem.quantity}
                    onChange={e => setSparePartItem(p => ({ ...p, quantity: e.target.value }))}
                    style={{ ...inp, marginBottom:6 }} />
                  <select value={sparePartItem.machine_id}
                    onChange={e => setSparePartItem(p => ({ ...p, machine_id: e.target.value }))}
                    style={{ ...inp, marginBottom:6 }}>
                    <option value="">Select Machine (optional)</option>
                    {siteMachinesForDefective.map(m => (
                      <option key={m.id} value={m.id}>{m.machine_name || "Unnamed"} {m.model_no ? `· ${m.model_no}` : ""}</option>
                    ))}
                  </select>
                  <input placeholder="Notes (optional)" value={sparePartItem.notes}
                    onChange={e => setSparePartItem(p => ({ ...p, notes: e.target.value }))}
                    style={{ ...inp, marginBottom:8 }} />
                  <button onClick={submitSparePart} disabled={submittingSparePart}
                    style={{ background:"#0891B2", color:"#fff", border:"none", borderRadius:8, padding:"10px 0", width:"100%", fontSize:13, fontWeight:600, cursor:"pointer" }}>
                    {submittingSparePart ? "Submitting..." : "✅ Submit Request"}
                  </button>
                </div>
              )}
            </div>
          )}

          {/* DEFECTIVE ITEMS LIST */}
          {service.defective_items?.length > 0 && (
            <div style={card}>
              <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between" }}>
                <span style={label}>Defective Items ({service.defective_items.length})</span>
                {isManager && service.defective_items.some(item => !item.oem_return_status || item.oem_return_status === "pending") && (
                  <button
                    onClick={() => navigate("/oem-returns", { state: { preselect_service_id: service.id } })}
                    style={{ background:"none", border:"none", color:"#DC2626", fontSize:11, fontWeight:700, cursor:"pointer", padding:0, marginBottom:10 }}>
                    📦 Send to OEM →
                  </button>
                )}
              </div>
              {service.defective_items.map((item,i)=>{
                const alreadyRequested = sparePartRequests.some(
                  r => r.linked_defective_item_id === String(i)
                );
                return (
                <div key={i} style={{ background:"#FEF2F2", border:"1px solid #FECACA", borderRadius:8, padding:"8px 10px", marginBottom:6 }}>
                  <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between" }}>
                    <div style={{ fontSize:13, fontWeight:600, color:"#991B1B" }}>{item.item_name} × {item.qty}</div>
                    {item.oem_return_status && item.oem_return_status !== "pending" && (
                      <span style={{ fontSize:10, fontWeight:700, color:"#166534", background:"#DCFCE7", padding:"2px 8px", borderRadius:10 }}>
                        {item.oem_return_status.replace(/_/g," ")}
                      </span>
                    )}
                  </div>
                  {item.machine_model && <div style={{ fontSize:11, color:"#6B7280" }}>Model: {item.machine_model}</div>}
                  {item.serial_no     && <div style={{ fontSize:11, color:"#6B7280" }}>S/N: {item.serial_no}</div>}
                  {item.part_name     && <div style={{ fontSize:11, color:"#6B7280" }}>Brand: {item.part_name} {item.part_code&&`(${item.part_code})`}</div>}
                  {isManager && (
                    alreadyRequested ? (
                      <div style={{ marginTop:6, fontSize:11, fontWeight:700, color:"#16A34A" }}>
                        ✓ Spare Part Already Requested
                      </div>
                    ) : (
                      <button
                        onClick={() => prefillSparePartFromDefective(item, i)}
                        style={{ display:"block", marginTop:6, background:"none", border:"none", color:"#7C3AED", fontSize:11, fontWeight:700, cursor:"pointer", padding:0 }}>
                        🔩 Request Replacement Part →
                      </button>
                    )
                  )}
                </div>
                );
              })}
            </div>
          )}

          {/* SPARE PART REQUESTS — read-only here; raised from technician's
              TaskDetail.js, triaged (Warranty/Buy/Already-With-Us/Repair)
              from the Store's Spare Part Requests dashboard. */}
          {sparePartRequests.length > 0 && (
            <div style={card}>
              <span style={label}>🔩 Spare Part Requests ({sparePartRequests.length})</span>
              {sparePartRequests.map((r) => (
                <div key={r.id} style={{ background:"#ECFEFF", border:"1px solid #67E8F9", borderRadius:8, padding:"8px 10px", marginBottom:6 }}>
                  <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between" }}>
                    <div style={{ fontSize:13, fontWeight:600, color:"#0E7490" }}>{r.part_name} × {r.quantity}</div>
                    <span style={{ fontSize:10, fontWeight:700, color:"#0E7490", background:"#CFFAFE", padding:"2px 8px", borderRadius:10 }}>
                      {r.status.replace(/_/g," ")}
                    </span>
                  </div>
                  {r.part_no    && <div style={{ fontSize:11, color:"#6B7280" }}>Part No: {r.part_no}</div>}
                  {r.serial_no  && <div style={{ fontSize:11, color:"#6B7280" }}>S/N: {r.serial_no}</div>}
                  {r.brand      && <div style={{ fontSize:11, color:"#6B7280" }}>Brand: {r.brand}</div>}
                  <div style={{ fontSize:11, color:"#0891B2", marginTop:2 }}>{r.request_no} · {r.resolution_type !== "pending" ? r.resolution_type.replace(/_/g," ") : "awaiting triage"}</div>
                </div>
              ))}
              {isManager && (
                <button
                  onClick={() => navigate("/spare-part-requests", { state: { preselect_service_id: service.id } })}
                  style={{ background:"none", border:"none", color:"#0E7490", fontSize:11, fontWeight:700, cursor:"pointer", padding:0, marginTop:4 }}>
                  🔩 Manage in Spare Part Requests →
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      

      {/* ── CANCEL JOB (manager only) ── */}
      {isManager && !["closed","cancelled"].includes(service.status) && (
        <div style={{ ...card, marginTop:14, display:"flex", alignItems:"center", justifyContent:"space-between", flexWrap:"wrap", gap:10 }}>
          <span style={{ fontSize:12, color:"#6B7280" }}>
            Manager Actions — Cancel this job if created by mistake or no longer needed.
          </span>
          <button
            onClick={async () => {
              const reason = window.prompt("Cancellation reason (required):");
              if (!reason?.trim()) return;
              if (!window.confirm("Are you sure you want to cancel this job? This cannot be undone.")) return;
              try {
                await updateDoc(doc(db, "service_tasks", id), {
                  status:            "cancelled",
                  cancelled_reason:  reason.trim(),
                  cancelled_by:      user.uid || user.id,
                  cancelled_by_name: user.name,
                  cancelled_at:      serverTimestamp(),
                  updated_at:        serverTimestamp(),
                });

                // Tech ko notification bhejo — agar assigned hai
                const techUid = service.assigned_techs?.[0]?.id || service.assigned_to;
                if (techUid) {
                  await addDoc(collection(db, "notifications"), {
                    type:               "job_cancelled",
                    notification_class: "informational",
                    title:              "Job Cancelled",
                    body:               `${service.complaint || "Service Job"} at ${service.site_name || ""} has been cancelled. Reason: ${reason.trim()}`,
                    for_roles:          ["technician"],
                    for_uid:            techUid,
                    company_id:         user.company_id,
                    reference_id:       id,
                    reference_type:     "service_task",
                    route:              null,
                    is_read:            true,
                    resolved:           true,
                    created_at:         serverTimestamp(),
                  });
                }

                alert("✅ Job Cancelled");
                loadService();
              } catch (err) { alert("Error: " + err.message); }
            }}
            style={{ background:"#6B7280", color:"#fff", border:"none", borderRadius:8, padding:"9px 18px", fontSize:13, fontWeight:600, cursor:"pointer", whiteSpace:"nowrap" }}>
            ✕ Cancel Job
          </button>
        </div>
      )}

      {/* ── COMPLETION REVIEW (full width, manager only) ── */}
      {isManager && service.status === "completed_pending_approval" && (
        <div style={{ ...card, marginTop:14, borderColor:"#DDD6FE", borderWidth:2 }}>
          <span style={{ ...label, color:"#7C3AED" }}>📋 Service Report — Pending Approval</span>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:14 }}>
            <div>
              {[{l:"Issue Found",v:service.issue_found},{l:"Work Done",v:service.resolution},{l:"Summary",v:service.service_report},{l:"Completion",v:service.completion_type==="complete"?"✅ Complete":"⚡ Partial"}].filter(r=>r.v).map((r,i)=>(
                <div key={i} style={{ display:"flex", gap:8, padding:"6px 0", borderBottom:"1px solid #F9FAFB" }}>
                  <span style={{ fontSize:12, color:"#9CA3AF", width:90, flexShrink:0 }}>{r.l}</span>
                  <span style={{ fontSize:13, color:"#111827", fontWeight:500 }}>{r.v}</span>
                </div>
              ))}
            </div>
            <div>
              {service.site_payment_received ? (
                <div style={{ background:"#F0FDF4", border:"1px solid #BBF7D0", borderRadius:10, padding:12 }}>
                  <div style={{ fontSize:12, fontWeight:700, color:"#166534", marginBottom:6 }}>💰 Payment Collected</div>
                  <div style={{ fontSize:14, fontWeight:700, color:"#111827" }}>₹{Number(service.site_payment_amount||0).toLocaleString()}</div>
                  <div style={{ fontSize:12, color:"#6B7280" }}>{service.site_payment_mode} · By {service.site_payment_by||"—"}</div>
                </div>
              ) : (
                <div style={{ background:"#FEF2F2", border:"1px solid #FECACA", borderRadius:10, padding:12 }}>
                  <div style={{ fontSize:12, fontWeight:700, color:"#991B1B" }}>❌ No payment collected</div>
                </div>
              )}
            </div>
          </div>

          <div style={{ display:"flex", gap:10, marginTop:14 }}>
            <button onClick={() => {
  // If the technician already collected site payment, pre-select
  // "Chargeable Service" and pre-fill it as the FIRST line item —
  // Manager can rename the description, add more line items, or
  // change the amount before confirming close.
  setCloseData(p => ({
    ...p,
    job_type: service.site_payment_received ? "chargeable_service" : p.job_type,
    is_chargeable: service.site_payment_received ? true : p.is_chargeable,
    line_items: service.site_payment_received
      ? [{ description: "Site Collection", amount: String(service.site_payment_amount || "") }]
      : p.line_items,
  }));
  setShowCloseForm(true);
}}
              style={{ flex:1, background:"#16A34A", color:"#fff", border:"none", borderRadius:8, padding:"11px 0", fontSize:14, fontWeight:700, cursor:"pointer" }}>
              ✅ Close Job
            </button>
            <button onClick={()=>setShowRejectForm(true)}
              style={{ flex:1, background:"#DC2626", color:"#fff", border:"none", borderRadius:8, padding:"11px 0", fontSize:14, fontWeight:700, cursor:"pointer" }}>
              ↩️ Send Back
            </button>
          </div>

          {showCloseForm && (
            <div style={{ background:"#F0FDF4", border:"1px solid #BBF7D0", borderRadius:10, padding:14, marginTop:12 }}>
              <div style={{ fontSize:13, fontWeight:600, color:"#166534", marginBottom:10 }}>Close Job Details</div>
              <select
  value={closeData.job_type}
  onChange={e => {
    const jt = e.target.value;
    const isChargeable = jt === "chargeable_service";
    setCloseData(p => ({
      ...p,
      job_type: jt,
      is_chargeable: isChargeable,
    }));
  }}
  style={{ ...inp, marginBottom: 8 }}
>
  <option value="">Select Job Type *</option>
  <option value="chargeable_service">💰 Chargeable Service</option>
  <option value="amc">📋 AMC Visit</option>
  <option value="warranty">🛡 Warranty</option>
  <option value="project">🏗 Project Work</option>
  <option value="internal">🏢 Internal</option>
  <option value="not_chargeable">🆓 Not Chargeable</option>
</select>

{closeData.job_type === "chargeable_service" && (
  <>
    <div style={{ marginBottom: 8 }}>
      <div style={{ fontSize:12, fontWeight:600, color:"#166534", marginBottom:6 }}>
        Line Items (e.g. "Gas Charging" — ₹2000, "Cleaning & Leak Check" — ₹2000)
      </div>
      {closeData.line_items.map((li, i) => (
        <div key={i} style={{ display:"flex", gap:6, marginBottom:6 }}>
          <input
            placeholder="Description"
            value={li.description}
            onChange={e => updateCloseLineItem(i, "description", e.target.value)}
            style={{ ...inp, flex:2 }}
          />
          <input
            type="number"
            placeholder="₹ Amount"
            value={li.amount}
            onChange={e => updateCloseLineItem(i, "amount", e.target.value)}
            style={{ ...inp, flex:1 }}
          />
          {closeData.line_items.length > 1 && (
            <button type="button" onClick={() => removeCloseLineItem(i)}
              style={{ background:"none", border:"none", color:"#DC2626", fontSize:16, cursor:"pointer", padding:"0 4px" }}>
              ✕
            </button>
          )}
        </div>
      ))}
      <button type="button" onClick={addCloseLineItem}
        style={{ background:"none", border:"1px dashed #16A34A", color:"#16A34A", borderRadius:8, padding:"6px 12px", fontSize:12, fontWeight:600, cursor:"pointer", width:"100%" }}>
        + Add Line Item
      </button>
      <div style={{ textAlign:"right", fontSize:13, fontWeight:700, color:"#166534", marginTop:6 }}>
        Total: ₹{closeLineItemsTotal.toLocaleString("en-IN")}
      </div>
    </div>
    </>
)}
              <div style={{ display:"flex", gap:8 }}>
                <button onClick={()=>setShowCloseForm(false)} style={{ flex:1, background:"#fff", border:"1px solid #E5E7EB", borderRadius:8, padding:"9px 0", fontSize:13, cursor:"pointer", color:"#6B7280" }}>Cancel</button>
                <button onClick={handleCloseJob} disabled={closing} style={{ flex:1, background:"#16A34A", color:"#fff", border:"none", borderRadius:8, padding:"9px 0", fontSize:13, fontWeight:600, cursor:"pointer" }}>{closing?"Closing...":"Confirm Close"}</button>
              </div>
            </div>
          )}

          {showRejectForm && (
            <div style={{ background:"#FEF2F2", border:"1px solid #FECACA", borderRadius:10, padding:14, marginTop:12 }}>
              <div style={{ fontSize:13, fontWeight:600, color:"#991B1B", marginBottom:10 }}>Send Back to Technician</div>
              <textarea placeholder="Reason *" value={rejectReason} onChange={e=>setRejectReason(e.target.value)} rows={3} style={{ ...inp, resize:"none", marginBottom:8 }} />
              <div style={{ display:"flex", gap:8 }}>
                <button onClick={()=>{setShowRejectForm(false);setRejectReason("");}} style={{ flex:1, background:"#fff", border:"1px solid #E5E7EB", borderRadius:8, padding:"9px 0", fontSize:13, cursor:"pointer", color:"#6B7280" }}>Cancel</button>
                <button onClick={handleRejectCompletion} style={{ flex:1, background:"#DC2626", color:"#fff", border:"none", borderRadius:8, padding:"9px 0", fontSize:13, fontWeight:600, cursor:"pointer" }}>Confirm Send Back</button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
